import { BabylonjsModule } from '@modules/babylonjs/src/BabylonjsModule';
import { Layers } from '@modules/core';
import { useZed } from '@modules/core/src/react/useZed';
import { StandardModule } from '@modules/standard/src/StandardModule';
import zams from '@zams/logger/zed';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getTourDanjiZamsParams, TourDanjiModule } from '../TourDanjiModule';

import fallIcon from './assets/daylight_fall.png';
import dayLightIcon from './assets/daylight_icon.png';
import pauseButton from './assets/daylight_pause.svg';
import playButton from './assets/daylight_play.svg';
import springIcon from './assets/daylight_spring.png';
import summerIcon from './assets/daylight_summer.png';
import winterIcon from './assets/daylight_winter.png';
import { useScreen } from './hooks/useScreen';
import styles from './styles/DaylightPanel.module.css';


type SeasonType = '봄' | '여름' | '가을' | '겨울';
type PlayButtonStatus = '재생' | '일시정지';

export function DaylightPanel() {
  const seasons = ['봄', '여름', '가을', '겨울'];
  const seasonIcons = [springIcon, summerIcon, fallIcon, winterIcon];
  const zed = useZed<[BabylonjsModule, StandardModule, TourDanjiModule]>();
  const [isTogglePanel, setTogglePanel] = useState<boolean>(false);
  const [isPlaying, setPlaying] = useState<boolean>(false);
  const [seasonIndex, setSeasonIndex] = useState<number>(0);
  const [time, setTime] = useState<Date>(new Date());
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const isFirst = useRef(true);
  const screenInfo = useScreen();

  useEffect(() => {
    if (!zed) return;

    setTime(zed.api.getCurrentDate());

    const onCameraViewTypeChanged = zed.api.observer.onCameraViewTypeChanged.add((data) => {
      // 실내뷰에서는 InteriorViewDaylightPanel 컴포넌트를 보여준다.
      setIsVisible(data !== 'UnitWindowView');
    });

    return () => {
      onCameraViewTypeChanged.remove();
    };
  }, [zed]);

  useEffect(() => {
    if (zed?.api.observer.onTimeIsRunning.hasObservers()) {
      zed?.api.observer.onTimeIsRunning.notifyObservers(isPlaying);
    }
  }, [isPlaying]);

  useEffect(() => {
    if (!zed) return;
    const timerId = setInterval(() => {
      setTime(zed.api.getCurrentDate());
    }, 100);
    return () => {
      clearInterval(timerId);
    };
  }, [zed]);

  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }

    if (isTogglePanel) {
      zams.z137({
        ...getZamsParams(),
      }).send();
    }
  }, [isTogglePanel]);

  const handleSeasonChange = () => {
    const newSeason = (seasonIndex + 1) % seasons.length;
    zams.z138({
      ...getZamsParams(),
      status: seasons[newSeason] as SeasonType,
    }).send();
    setSeasonIndex(newSeason);
    if (zed?.api.observer.onSeasonChanged.hasObservers()) {
      zed?.api.observer.onSeasonChanged.notifyObservers(newSeason);
    }
  }

  const handleTimeChange = () => {
    const season = seasons[seasonIndex] as SeasonType;
    const buttonName = isPlaying ? '일시정지' : '재생';
    setPlaying(!isPlaying);
    zams.z139({
      ...getZamsParams(),
      status: season,
      button_name: buttonName as PlayButtonStatus,
    }).send();
  }

  const getZamsParams = useCallback(() => {
    type ScreenStatus = '실내' | '실외';
    return {
      ...getTourDanjiZamsParams(),
      screen_status: '실외' as ScreenStatus,
    }
  }, []);

  const { isDesktop } = screenInfo;

  if (!isVisible) return null;

  const controlPanelStyle = isDesktop ? styles.controlPanelWWW : styles.controlPanel;
  const toogleButtonStyle = isDesktop ? styles.toogleButtonWWW : styles.toogleButton;
  const hour = time.getHours() > 12 ? time.getHours() - 12 : time.getHours();

  return (
    <div style={{ zIndex: Layers.UI_CONTENTS }} className={styles.root}>
      <div className={toogleButtonStyle}
        onClick={() => {
          zams.z136({
            ...getZamsParams(),
            status: !isTogglePanel ? '1' : '0',
          }).send();
          setTogglePanel(!isTogglePanel);
        }}>
        {isTogglePanel && 
        <div className={styles.toggleOn}></div>}
        <div className={styles.iconContainer}>
          <img src={dayLightIcon} className={styles.icon} />
          <div className={styles.iconText}>{'일조량'}</div>
        </div>
      </div>
      {isTogglePanel &&
      <div className={controlPanelStyle}>
        <div className={styles.panelContainer}>
          <div className={styles.panelTimerContainer}>
            <div className={styles.panelTimer}>
            <div className={styles.panelTimerLargeText}>{`${hour.toString().padStart(2, '0')}`}</div>
              <div className={styles.panelTimerColonText}>
                {':'}
              </div>
              <div className={styles.panelTimerText}>
                {`${time.getMinutes().toString().padStart(2, '0')}`}
              </div>
              <div className={styles.panelTimerTextRightUpper}>{`${time.getHours() >= 12 ? 'PM' : 'AM'}`}</div>
            </div>      
            <div className={styles.panelTimerButton}
              onClick={handleTimeChange}>
              {isPlaying ?
              <img src={pauseButton} className={styles.panelTimerButtonIcon} /> :
                <img src={playButton} className={styles.panelTimerButtonIcon} />}
              </div>      
          </div>
          <div className={styles.panelSeasonButtonContainer}>
            <div 
              className={styles.panelSeasonButton}
              onClick={handleSeasonChange}>
              <div className={styles.panelSeasonButtonContentsContainer}>
              <img src={seasonIcons[seasonIndex]} className={styles.panelSeasonButtonIcon} />
                <div className={styles.panelSeasonButtonText}>{seasons[seasonIndex]}</div>
              </div>
            </div>
          </div>
          </div>
        </div>
      }
    </div>
  );
}
